import React, { useState } from 'react';
import { Phone, MapPin, Mail, Clock, MessageCircle, CalendarCheck, AlertCircle, Video } from 'lucide-react';
import { PORTAL_URL, PHONE_DISPLAY, PHONE_TEL, LEGAL } from '../SiteLayout';
import PageSEO from '../../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQS = [
  { q: 'Puis-je venir sans rendez-vous ?', a: "Oui. Cependant, la prise de rendez-vous est recommandée afin de réduire votre temps d'attente." },
  { q: 'Puis-je contacter un médecin directement ?', a: "Les demandes médicales sont orientées par notre équipe afin de garantir une prise en charge adaptée." },
  { q: "Puis-je envoyer mes résultats d'examens ?", a: "Oui. Selon votre situation, ils pourront être transmis via votre espace patient, par e-mail sécurisé ou lors d'une téléconsultation." },
  { q: "Combien de temps faut-il pour obtenir une réponse ?", a: "Notre objectif est de répondre aux demandes dans les meilleurs délais pendant les heures d'ouverture du Centre." },
];

export default function SiteContact() {
  const [form, setForm] = useState({ name: '', firstname: '', phone: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Envoi impossible pour le moment.');
      }
      setSent(true);
    } catch (err) {
      setError(err.message || 'Envoi impossible pour le moment.');
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="site-contact">
      <PageSEO
        title="Contact — Matadi & Boma, Kongo Central"
        description="Contactez le Centre Médical la Trinité à Matadi (Avenue Nkala-nkala) ou Boma (Avenue du Fleuve). Téléphone : +243 840 061 625. Urgences 24h/24."
        path="/contact"
      />
      <div className="site-page-head" style={{ backgroundImage: "linear-gradient(rgba(13, 44, 84, 0.85), rgba(21, 101, 192, 0.75)), url('/photos/facade_principale.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="site-page-head-inner">
          <h1 className="site-reveal">Nous sommes à votre écoute</h1>
          <p className="site-reveal-1">
            Une question ? Besoin d&apos;un renseignement ? Vous souhaitez prendre rendez-vous ou obtenir
            des informations sur nos services ? Notre équipe est disponible pour vous accompagner et
            répondre à vos demandes dans les meilleurs délais.
          </p>
        </div>
      </div>

      <section className="site-section">
        <span className="site-kicker">Nos coordonnées</span>
        <h2>Comment nous joindre ?</h2>
        <div className="site-contact-grid" style={{ marginTop: '1.5rem' }}>
          <div className="site-contact-card" data-testid="contact-phone-card">
            <h3><Phone size={19} /> Téléphone & Urgences</h3>
            <p>Urgences assurées 24h/24, 7j/7.</p>
            <a href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a>
          </div>
          <div className="site-contact-card" data-testid="contact-whatsapp-card">
            <h3><MessageCircle size={19} /> WhatsApp</h3>
            <p>Écrivez-nous, nous répondons rapidement.</p>
            <a href={`https://wa.me/${PHONE_TEL.replace('+', '')}`} target="_blank" rel="noreferrer">Discuter sur WhatsApp</a>
          </div>
          <div className="site-contact-card" data-testid="contact-address-card">
            <h3><MapPin size={19} /> Siège de Matadi</h3>
            <p>Centre Médical la Trinité SARLU<br />{LEGAL.addressMatadi}<br />Kongo Central — RDC</p>
          </div>
          <div className="site-contact-card">
            <h3><MapPin size={19} /> Extension de Boma</h3>
            <p>{LEGAL.addressBoma}<br />Kongo Central — RDC</p>
          </div>
          <div className="site-contact-card" data-testid="contact-hours-card">
            <h3><Clock size={19} /> Horaires d&apos;ouverture</h3>
            <p>
              Consultations : lundi – samedi, 8h00 – 17h00<br />
              Dimanche : selon les services concernés<br />
              <strong>Urgences : 24h/24, 7j/7</strong>
            </p>
          </div>
          <div className="site-contact-card" data-testid="contact-email-card">
            <h3><Mail size={19} /> Email</h3>
            <p>Pour toute demande administrative ou partenariat.</p>
            <a href="mailto:contact@cmlatrinite-matadi.com">contact@cmlatrinite-matadi.com</a>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <span className="site-kicker">Écrivez-nous</span>
            <h2>Envoyez-nous votre demande</h2>
            <p style={{ color: 'var(--site-muted)' }}>
              Vous souhaitez obtenir un renseignement ou être recontacté ? Complétez le formulaire ci-dessous.
              Notre équipe vous répondra dans les meilleurs délais.
            </p>
            {sent ? (
              <div data-testid="contact-form-success" style={{ marginTop: 20, padding: 20, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 10, color: '#166534' }}>
                <strong>Merci pour votre message.</strong><br />
                Votre demande a bien été enregistrée. Notre équipe vous répondra dans les meilleurs délais.
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" style={{ marginTop: 20, display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input required placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1' }} data-testid="contact-name" />
                  <input required placeholder="Prénom" value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1' }} data-testid="contact-firstname" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input required type="tel" placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1' }} data-testid="contact-phone" />
                  <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1' }} data-testid="contact-email" />
                </div>
                <input placeholder="Objet" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1' }} data-testid="contact-subject" />
                <textarea required placeholder="Votre message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} style={{ padding: 12, borderRadius: 8, border: '1px solid #cbd5e1', fontFamily: 'inherit', resize: 'vertical' }} data-testid="contact-message" />
                {error && (
                  <div data-testid="contact-form-error" style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 14 }}>
                    {error}
                  </div>
                )}
                <button type="submit" className="site-btn site-btn-primary" disabled={sending} data-testid="contact-submit">
                  {sending ? 'Envoi en cours…' : 'Envoyer'}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>

      {/* Urgences */}
      <section className="site-section">
        <div style={{ background: '#fee2e2', borderLeft: '4px solid #dc2626', padding: '1.5rem 2rem', borderRadius: 10 }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#991b1b', margin: '0 0 .75rem' }}>
            <AlertCircle size={26} /> Une urgence médicale ?
          </h2>
          <p style={{ color: '#7f1d1d', marginBottom: '1rem' }}>
            En cas d&apos;urgence mettant en jeu le pronostic vital, contactez immédiatement notre service
            d&apos;urgence ou les services de secours compétents. Si votre état nécessite une prise en charge
            immédiate, présentez-vous directement au Centre Médical la Trinité ou dans l&apos;établissement
            de santé le plus proche. <strong>La téléconsultation ne remplace pas la prise en charge des urgences médicales.</strong>
          </p>
          <a href={`tel:${PHONE_TEL}`} className="site-btn site-btn-primary" style={{ background: '#dc2626' }} data-testid="contact-emergency-btn">
            <Phone size={16} /> Appeler les urgences — {PHONE_DISPLAY}
          </a>
        </div>
      </section>

      {/* FAQ */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Questions fréquentes</span>
          <h2>Vos questions, nos réponses</h2>
          <div style={{ marginTop: '1.5rem', display: 'grid', gap: 10 }}>
            {FAQS.map((f, i) => (
              <details key={i} style={{ padding: '1rem 1.25rem', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{f.q}</summary>
                <p style={{ marginTop: '.5rem', color: 'var(--site-muted)' }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>

      {/* CTA final */}
      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>Nous serons heureux de vous accueillir</h2>
            <p>Prenez rendez-vous en ligne ou en téléconsultation, ou contactez-nous par WhatsApp.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <a href={`${PORTAL_URL}?page=booking`} className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="contact-cta-booking-btn">
              <CalendarCheck size={18} /> Prendre rendez-vous
            </a>
            <a href="/teleconsultation" className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="contact-cta-tele-btn">
              <Video size={18} /> Téléconsultation
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
